import { FREE_SHIPPING_THRESHOLD, GIFT_THRESHOLD } from "src/configs/currencies";

export interface FreeShippingState {
  /** The market's threshold, in minor units. */
  thresholdCents: number;
  /** Already over the line. */
  qualified: boolean;
  /** Minor units still needed. 0 once qualified. */
  remainingCents: number;
  /** 0–100, for a progress bar. */
  progressPct: number;
}

/**
 * How far a basket is from free delivery.
 *
 * Kept out of the component because it decides money-adjacent copy — "you are
 * €12 away" is a promise — and because the same answer is needed in the cart,
 * on the delivery step, and in tests.
 *
 * Returns null when there is nothing honest to say: no threshold configured
 * for the market, or an empty basket. An empty cart showing "spend €80 more
 * for free shipping" is noise at the moment the customer has done nothing yet.
 *
 * The threshold is read from the domain-keyed table, so a market can be given
 * its own line without touching this. Note the backend still derives the
 * charged threshold from currency, so the two only agree while every market on
 * a currency shares a number — changing one market here without the backend
 * would promise a discount checkout does not honour.
 */
export function freeShippingState(
  subtotalCents: number,
  domain: string | null | undefined,
): FreeShippingState | null {
  const table = FREE_SHIPPING_THRESHOLD as Record<string, number | undefined>;
  const thresholdCents = domain ? table[domain] ?? 0 : 0;

  if (!thresholdCents || thresholdCents <= 0) return null;
  if (!Number.isFinite(subtotalCents) || subtotalCents <= 0) return null;

  const qualified = subtotalCents >= thresholdCents;
  const remainingCents = qualified ? 0 : thresholdCents - subtotalCents;

  return {
    thresholdCents,
    qualified,
    remainingCents,
    progressPct: Math.max(
      0,
      Math.min(100, Math.round((subtotalCents / thresholdCents) * 100)),
    ),
  };
}

/**
 * The market's gift line in minor units, or 0 when it has none or the gift is
 * switched off (`freeEnlargementEnabled`, see useFreeEnlargementEnabled). The
 * backend only adds the free enlargement while the switch is on, so the track
 * must never promise it otherwise.
 */
function giftCents(domain: string | null | undefined, giftEnabled: boolean): number {
  if (!giftEnabled || !domain) return 0;
  return GIFT_THRESHOLD[domain] ?? 0;
}

export type RewardGoalKind = "gift" | "freeShipping";

export interface RewardGoal {
  kind: RewardGoalKind;
  /** Minor units still needed to reach it. Always > 0. */
  shortCents: number;
  thresholdCents: number;
  /** 0–100, for a progress bar. */
  progressPct: number;
  /** The gift is already earned (only meaningful when kind is freeShipping). */
  giftEarned: boolean;
}

/**
 * The next reward the basket can reach: the gift first, then free shipping.
 *
 * One goal at a time, deliberately. Two bars saying "spend more" read as one
 * repeated element, and the nearer goal is the one that moves people — the
 * free-shipping line is more than twice the average basket.
 *
 * Returns null once nothing is left to reach (or the market has neither).
 */
export function nextRewardGoal(
  subtotalCents: number,
  domain: string | null | undefined,
  giftEnabled: boolean,
): RewardGoal | null {
  if (!Number.isFinite(subtotalCents) || subtotalCents <= 0) return null;
  const gift = giftCents(domain, giftEnabled);
  const giftEarned = gift > 0 && subtotalCents >= gift;
  if (gift > 0 && !giftEarned) {
    return {
      kind: "gift",
      shortCents: gift - subtotalCents,
      thresholdCents: gift,
      progressPct: Math.max(0, Math.min(100, Math.round((subtotalCents / gift) * 100))),
      giftEarned: false,
    };
  }
  const shipping = freeShippingState(subtotalCents, domain);
  if (shipping && !shipping.qualified) {
    return {
      kind: "freeShipping",
      shortCents: shipping.remainingCents,
      thresholdCents: shipping.thresholdCents,
      progressPct: shipping.progressPct,
      giftEarned,
    };
  }
  return null;
}

/** Whether this market has a gift at all, and whether the basket earned it. */
export function giftEarned(
  subtotalCents: number,
  domain: string | null | undefined,
  giftEnabled: boolean,
): boolean {
  const gift = giftCents(domain, giftEnabled);
  return gift > 0 && subtotalCents >= gift;
}

/** The market's reward stops in order: the gift (where there is one), then free shipping. */
export function rewardStops(domain: string | null | undefined, giftEnabled: boolean) {
  if (!domain) return [];
  const shipping =
    (FREE_SHIPPING_THRESHOLD as Record<string, number | undefined>)[domain] ??
    0;
  const gift = giftCents(domain, giftEnabled);
  const stops: { kind: "gift" | "freeShipping"; cents: number }[] = [];
  if (gift > 0 && (!shipping || gift < shipping))
    stops.push({ kind: "gift", cents: gift });
  if (shipping > 0) stops.push({ kind: "freeShipping", cents: shipping });
  return stops;
}
